import React from 'react';

import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';
import { token } from '@atlaskit/tokens';

import { Divider } from '../divider';

const styles = cssMap({
	menuContainer: {
		width: '100%',
		display: 'inline-flex',
		paddingTop: token('space.100'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.100'),
		flexDirection: 'column',
		alignItems: 'center',
		gap: token('space.025'),
		backgroundColor: token('elevation.surface'),
	},
	menuTextContainer: {
		display: 'flex',
		paddingTop: token('space.100'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.100'),
		alignItems: 'center',
		gap: token('space.150'),
		alignSelf: 'stretch',
	},
	menuText: {
		font: token('font.heading.xxsmall'),
		color: token('color.text.subtlest'),
	},
	menuList: {
		width: '100%',
		display: 'flex',
		marginTop: token('space.0'),
		marginRight: token('space.0'),
		marginBottom: token('space.0'),
		marginLeft: token('space.0'),
		paddingTop: token('space.0'),
		paddingRight: token('space.0'),
		paddingBottom: token('space.0'),
		paddingLeft: token('space.0'),
		flexDirection: 'column',
		alignItems: 'center',
		gap: token('space.025'),
		listStyle: 'none',
	},
	menuListItem: {
		width: '100%',
		display: 'flex',
		marginTop: token('space.0'),
		marginRight: token('space.0'),
		marginBottom: token('space.0'),
		marginLeft: token('space.0'),
		paddingTop: token('space.0'),
		paddingRight: token('space.0'),
		paddingBottom: token('space.0'),
		paddingLeft: token('space.0'),
	},
});

export type MenuItemComponent = React.ReactNode;

export interface MenuSection {
	title?: string;
	items: MenuItemComponent[];
}

export interface MenuContentSection {
	title?: string;
	content: React.ReactNode;
}

export type ChatHeaderMenuSection = MenuSection | MenuContentSection;

export interface ChatHeaderMenuListProps {
	sections: ChatHeaderMenuSection[];
}

export const ChatHeaderMenuList = ({ sections }: ChatHeaderMenuListProps): React.JSX.Element => {
	const visibleSections = sections.filter((section) =>
		'content' in section ? section.content != null : section.items.length > 0,
	);

	return (
		<Box xcss={styles.menuContainer}>
			{visibleSections.map((section, sectionIndex) => (
				<React.Fragment key={sectionIndex}>
					{section.title && (
						<Box xcss={styles.menuTextContainer}>
							{/*
							 * Intentionally not an `h2` element. This menu can render inside
							 * an editor's content area (see `shouldRenderToParent`), where
							 * the editor's `& h2` UGC typography rule would override the
							 * menu style. `role`/`aria-level` keep the heading semantics.
							 */}
							<Box role="heading" aria-level={2} xcss={styles.menuText}>
								{section.title}
							</Box>
						</Box>
					)}
					{'content' in section ? (
						section.content
					) : (
						<Box as="ul" xcss={styles.menuList}>
							{React.Children.map(section.items, (item) => (
								<Box as="li" xcss={styles.menuListItem}>
									{item}
								</Box>
							))}
						</Box>
					)}
					{sectionIndex !== visibleSections.length - 1 && <Divider isThin withMargin />}
				</React.Fragment>
			))}
		</Box>
	);
};
